/**
 * Two-Factor Authentication Timer Utility
 * Manages 5-minute session timer with localStorage persistence
 */

const TWO_FACTOR_TIMER_KEY = "twoFactorSessionStartTime";
const TWO_FACTOR_TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const twoFactorTimerUtils = {
  /**
   * Start the two-factor timer
   * @returns {number} Start time timestamp
   */
  startTimer: () => {
    const startTime = Date.now();
    localStorage.setItem(TWO_FACTOR_TIMER_KEY, startTime.toString());
    return startTime;
  },

  /**
   * Get elapsed time since timer start
   * @returns {number|null} Elapsed time in milliseconds, null if timer not started
   */
  getElapsedTime: () => {
    const startTime = localStorage.getItem(TWO_FACTOR_TIMER_KEY);
    if (!startTime) return null;
    return Date.now() - parseInt(startTime, 10);
  },

  /**
   * Get remaining time before timeout
   * @returns {number|null} Remaining time in milliseconds, null if timer not started, 0 if expired
   */
  getRemainingTime: () => {
    const elapsed = twoFactorTimerUtils.getElapsedTime();
    if (elapsed === null) return null;
    const remaining = TWO_FACTOR_TIMEOUT_DURATION - elapsed;
    return remaining > 0 ? remaining : 0;
  },

  /**
   * Check if the timer has expired
   * @returns {boolean} True if timer has expired
   */
  isExpired: () => {
    const remaining = twoFactorTimerUtils.getRemainingTime();
    return remaining !== null && remaining <= 0;
  },

  /**
   * Clear the timer from localStorage
   */
  clearTimer: () => {
    localStorage.removeItem(TWO_FACTOR_TIMER_KEY);
  },

  /**
   * Check if timer exists in localStorage
   * @returns {boolean} True if timer is active
   */
  hasActiveTimer: () => {
    return localStorage.getItem(TWO_FACTOR_TIMER_KEY) !== null;
  },

  /**
   * Format time in minutes:seconds format
   * @param {number} milliseconds Time in milliseconds
   * @returns {string} Formatted time string (e.g., "4:32")
   */
  formatTime: (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return "0:00";
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  },

  /**
   * Get timeout duration constant
   * @returns {number} Timeout duration in milliseconds
   */
  getTimeoutDuration: () => TWO_FACTOR_TIMEOUT_DURATION,
};
